import { UserSession } from './user-session';
import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Logger, LoggerFactory } from './logger.service';
import { Site } from './site-context.service';
import { Subscription } from 'rxjs';

export { Site } from './site-context.service';
export { UserSession } from './user-session';

// Wraps UserSession and calls onSiteChanged method
// If you override ngOnInit() or ngOnDestroy() when deriving from the class, do not forget to call supers
@Component({
  template: '',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export abstract class SiteSpecific implements OnDestroy, OnInit {
  private lastSite: Site;
  private subject: Subscription;
  protected subscriptionArray: Subscription[] = [];
  private readonly _logger: Logger; // Using _ prefix otherwise it collides with logger from derived class.
  public isAdmin: boolean;
  constructor(
    protected readonly session: UserSession,
    loggerFactory: LoggerFactory,
  ) {
    // New instance in order to avoid injecting a all derived classes must inject this.
    this._logger = loggerFactory.get('SiteSpecific');
  }

  protected abstract onSiteChanged(site: Site | undefined);

  async ngOnInit() {
    this.isAdmin = this.session.context.isAdmin;
    this.lastSite = await this.session.siteContext.getCurrentSiteAsync();
    // Site can be undefined until it's loaded. But if not, inform the component.
    this.onSiteChanged(this.lastSite);
    // Listen to the future changes
    this.subject = this.session.siteContext.onCurrentSiteChanged.subscribe(
      async (site: Site) => await this.handleSiteChanged(site),
    );
  }

  private async handleSiteChanged(site: Site) {
    this.warnIfNotInitialized();
    if (site) {
      if (!this.session.identityContext.hasCurrentIdentity) {
        this._logger.error('Anonym user has a site.');
        return;
      }
      if (!this.session.identityContext.isUserVerified) {
        this._logger.error(`User is not verified anymore even though it has a site : ${JSON.stringify(site)}`);
      }
    }

    if (this.lastSite !== site) {
      this.lastSite = site;

      // Init onSiteChanged on component, only if we are logged in
      if (site) {
        await this.onSiteChanged(site);
      }
    }
  }

  public ngOnDestroy(): void {
    this.warnIfNotInitialized();
    this.subscriptionArray.push(this.subject);
    this.subscriptionArray.forEach((subs) => subs && subs.unsubscribe());
  }

  private warnIfNotInitialized() {
    if (!this.subject) {
      this._logger.warn('ngOnInit is never called. Did you forget to call super.ngOnInit if you override that class?');
    }
  }
}
